import {
  archiveOrderID,
  DEFAULT_BLOTTER_SELECTION_SINGLE_PARAMS,
  includesEnumValue,
  ORDER,
  removeEmptyFilters,
  selectArchivedOrderIDSet,
  selectFocusedOrderID,
  setFocusedOrderID,
  useAccordionFilterBuilder,
  useGetNewEntityWithTranslatedTextPipe,
  usePersistedBlotterTable,
  useWLOrderFormDispatch,
  useWLOrderFormSelector,
  useWsBlotterTable,
  type CustomerOrder,
  type OrdStatusEnum,
  type SideEnum,
  type WebsocketRequest,
} from '@talos/kyoko';
import { useCallback, useEffect, useMemo } from 'react';

import { onlyServerFilterKeys, useOrdersFilter } from '@src/components/Filters/FilterBuilder/useOrdersFilter';
import { ACTIVE_ORDER_STATUSES } from '@src/tokens/order';
import type { CellClickedEvent } from 'ag-grid-community';
import { useActiveOrdersBlotterColumn } from './useActiveOrdersBlotterColumns';

interface ActiveOrdersWebsocketRequest extends WebsocketRequest {
  MarketAccounts?: string[];
  Sides?: SideEnum[];
  Statuses?: OrdStatusEnum[];
  Strategies?: string[];
  Symbols?: string[];
}

type UseActiveOrdersBlotterProps = {
  maxRows: number;
  showOnlySelfOrdersAndTrades?: boolean;
};

const INITIAL_FILTER = { StartDate: Sugar.Date.create('a year ago') };

const DEFAULT_SORT = '-SubmitTime';

export function useActiveOrdersBlotter({ maxRows, showOnlySelfOrdersAndTrades = false }: UseActiveOrdersBlotterProps) {
  const archivedOrderIDs = useWLOrderFormSelector(selectArchivedOrderIDSet);
  const focusedOrderID = useWLOrderFormSelector(selectFocusedOrderID);
  const dispatch = useWLOrderFormDispatch();

  const columns = useActiveOrdersBlotterColumn();
  const persistedBlotterTable = usePersistedBlotterTable(`activeOrders`, {
    filter: INITIAL_FILTER,
  });

  // [DEAL-4595] Clear any saved Date Filter from the Active Orders Blotter
  const persistedFilter = persistedBlotterTable.state?.filter;
  delete persistedFilter?._start;
  delete persistedFilter?.StartDate;
  delete persistedFilter?.EndDate;
  // [DEAL-4595] Remove Date Filter from Active Orders Blotter
  const filteredOrders = useOrdersFilter({
    persistedBlotterTable,
    showDateRangePicker: false,
    showOnlySelfOrders: showOnlySelfOrdersAndTrades,
  });
  const { clientSideFilter: clientLocalFilter, blotterTableFilterProps, filterBuilderProps } = filteredOrders;

  const filterBuilderAccordion = useAccordionFilterBuilder({
    accordionProps: { initialOpen: false },
    filterBuilderProps,
  });

  const orderFormDispatch = useWLOrderFormDispatch();

  const handleOnCellClicked = useCallback(
    (event: CellClickedEvent<CustomerOrder, any>) => {
      if (event.data != null) {
        orderFormDispatch(setFocusedOrderID(event.data.OrderID));
      }
    },
    [orderFormDispatch]
  );

  const request = useMemo<ActiveOrdersWebsocketRequest>(
    () => ({
      name: ORDER,
      tag: 'ACTIVE_ORDERS',
      HideApiCalls: true,
      Throttle: '1s',
      sort_by: DEFAULT_SORT,
      ...removeEmptyFilters(onlyServerFilterKeys(filteredOrders.filter)),
      // Users cannot set their own statuses in this ActiveOrders blotter. We always set this set of statuses in the filter.
      Statuses: ACTIVE_ORDER_STATUSES,
    }),
    [filteredOrders.filter]
  );

  const translateCustomerOrderTextPipe = useGetNewEntityWithTranslatedTextPipe<CustomerOrder>();

  const blotterTable = useWsBlotterTable<ActiveOrdersWebsocketRequest, CustomerOrder>({
    request,
    columns,
    persistence: persistedBlotterTable,
    pipe: translateCustomerOrderTextPipe,
    rowID: 'OrderID',
    clientLocalFilter,
    flashRows: ['add'],
    startingRowLimit: maxRows,
    gridOptions: {
      rowSelection: DEFAULT_BLOTTER_SELECTION_SINGLE_PARAMS,
      animateRows: true,
      rowHeight: 32,
      onCellClicked: handleOnCellClicked,
    },
  });

  useEffect(() => {
    for (const blotterRow of blotterTable.getRows()) {
      if (archivedOrderIDs.has(blotterRow.data.OrderID)) {
        blotterRow.remove();
      }
    }
  }, [archivedOrderIDs, blotterTable]);

  useEffect(() => {
    for (const blotterRow of blotterTable.getRows()) {
      blotterRow.setSelected(blotterRow.data.OrderID === focusedOrderID);
    }
  }, [blotterTable, focusedOrderID]);

  const archiveAll = useCallback(() => {
    for (const blotterRow of blotterTable.getRows()) {
      if (!includesEnumValue(ACTIVE_ORDER_STATUSES, blotterRow.data.OrdStatus)) {
        dispatch(archiveOrderID(blotterRow.data.OrderID));
      }
    }
  }, [dispatch, blotterTable]);

  return useMemo(
    () => ({
      archiveAll,
      blotterTable,
      blotterTableFilterProps,
      filterBuilderAccordion,
    }),
    [archiveAll, blotterTable, blotterTableFilterProps, filterBuilderAccordion]
  );
}
